<!--
  Unified Mentions List Component
  
  Provides consistent UI for displaying filtered mentions with sections.
  Used by both hash mentions dropdown and tool-based popovers.
-->

<script setup lang="ts">
  import { computed, ref, watch, type PropType } from 'vue'

  import CPSkeleton from '@packages/ui/components/CPSkeleton.vue'
  import CPText from '@packages/ui/components/CPText.vue'
  import { useI18n } from 'vue-i18n'

  import { getChannelEmojiByType, hasChannelIcon, isChannelIconEmoji } from '@communities/channels-layer/utils/channel.helper'
  import type { IChannel } from '@communities/channels-layer/types/channels'

  import MentionEmptyState from './MentionEmptyState.vue'
  import MentionsListItem from './MentionsListItem.vue'
  import { useSuggestions } from './suggestionsService'
  import { useKeyboardNavigation } from '../composables/useKeyboardNavigation'

  import type { ChannelMentionIconType } from '../types/editor.types'
  import type { MentionItem, MentionSection } from '../types/mention.types'

  const props = defineProps({
    query: {
      type: String,
      default: '',
    },
    onSelect: {
      type: Function as PropType<(item: MentionItem) => void>,
      required: true,
    },
    showSections: {
      type: Boolean,
      default: false,
    },
    emptyMessage: {
      type: String,
      default: 'No mentions found',
    },
    filterType: {
      type: String as PropType<'channel' | 'event' | 'all'>,
      default: 'all',
    },
  })

  // Composables
  const { t } = useI18n()
  const suggestions = useSuggestions()

  // Helper function to resolve channel icon (same as existing logic)
  const resolveMentionIcon = (channel: Partial<IChannel> & { icon?: string }) => {
    if (channel.icon && isChannelIconEmoji(channel.icon)) {
      return { icon: channel.icon, iconType: 'emoji' as ChannelMentionIconType }
    }

    if (channel.icon && hasChannelIcon(channel.icon)) {
      return { icon: channel.icon, iconType: 'icon' as ChannelMentionIconType }
    }

    return {
      icon: getChannelEmojiByType(channel as IChannel),
      iconType: 'emoji' as ChannelMentionIconType,
    }
  }

  // Filtered data
  const filteredChannels = computed(() => {
    return suggestions.filterChannels(props.query || '')
  })

  const filteredEvents = computed(() => {
    return suggestions.filterEvents(props.query || '')
  })

  const filteredCourses = computed(() => {
    return suggestions.filterCourses(props.query || '')
  })

  // Loading state
  const isLoading = suggestions.isLoading

  // Courses section with mapped items
  const coursesSection = computed((): MentionSection => {
    return {
      title: 'Courses',
      items: filteredCourses.value.map(course => ({
        id: course.productId || course.id || '',
        label: course.title || '',
        type: 'course' as const,
        avatar: course.posterImage || '',
      })),
    }
  })

  // Events section with mapped items
  const eventsSection = computed((): MentionSection => {
    return {
      title: 'Events',
      items: filteredEvents.value.map(event => ({
        id: event.id,
        label: event.label,
        type: 'event' as const,
        timestamp: event.timestamp,
        startTime: event.startTime,
        endTime: event.endTime,
        nextOccurrence: event.nextOccurrence,
        isRecurring: event.isRecurring,
        eventCoverUrl: event.eventCoverUrl,
      })),
    }
  })

  // Channels section with mapped items
  const channelsSection = computed((): MentionSection => {
    return {
      title: 'Channels',
      items: filteredChannels.value.map(channel => {
        const mentionIcon = resolveMentionIcon(channel)
        return {
          id: channel.id || channel._id || '',
          label: channel.name,
          type: 'channel' as const,
          slug: channel.slug,
          icon: mentionIcon.icon,
          iconType: mentionIcon.iconType,
        }
      }),
    }
  })

  // All items flattened (for keyboard navigation)
  const allItems = computed((): MentionItem[] => {
    const items: MentionItem[] = []

    if (props.filterType === 'all') {
      items.push(...coursesSection.value.items)
      items.push(...eventsSection.value.items)
      items.push(...channelsSection.value.items)
    } else if (props.filterType === 'event') {
      items.push(...eventsSection.value.items)
    } else if (props.filterType === 'channel') {
      items.push(...channelsSection.value.items)
    }

    return items
  })

  // Handle item selection
  const handleSelect = (item: MentionItem) => {
    props.onSelect(item)
  }

  // Keyboard navigation using shared composable
  const keyboardNav = useKeyboardNavigation(allItems, handleSelect)
  const { selectedIndex } = keyboardNav

  /** DOM key path for list focus + integration tests (TipTap still uses exposed handleKeyDown). */
  const onListKeyDown = (event: KeyboardEvent) => {
    keyboardNav.handleKeyDown(event)
  }

  // Auto-scroll selected item into view
  const mentionsListRef = ref<HTMLElement>()
  watch(selectedIndex, () => {
    if (mentionsListRef.value) {
      const selectedElement = mentionsListRef.value.querySelector('.mention-list-item.selected')
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest', behavior: 'auto' })
      }
    }
  })

  // Expose keyboard handler and selected item for parent extension
  defineExpose({
    handleKeyDown: keyboardNav.handleKeyDown,
    getSelectedItem: () => allItems.value[selectedIndex.value] || null,
  })
</script>

<template>
  <div id="editor-mentions-list-root" ref="mentionsListRef" class="mentions-list" tabindex="0" @keydown="onListKeyDown">
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <div class="space-y-3 p-4">
        <div v-for="i in 3" :key="i" class="flex items-center gap-3">
          <CPSkeleton class="h-8 w-8 rounded-full" />
          <div class="flex-1 space-y-2">
            <CPSkeleton class="h-4 w-3/4" />
            <CPSkeleton class="h-3 w-1/2" />
          </div>
        </div>
      </div>
    </div>

    <!-- Results with Sections (with individual empty states) -->
    <div v-else-if="showSections" class="sectioned-results">
      <!-- Courses Section -->
      <div v-if="filterType === 'all'" class="section">
        <!-- Section Header -->
        <div class="section-header">
          <CPText size="xs" weight="medium" class="text-secondary">
            {{ t('editor.mentions.courses') }}
          </CPText>
        </div>

        <!-- Courses Items -->
        <div v-if="coursesSection.items.length > 0" class="section-items">
          <MentionsListItem
            v-for="item in coursesSection.items"
            :key="item.id"
            :item="item"
            :on-click="handleSelect"
            :is-selected="allItems.findIndex((i: MentionItem) => i.id === item.id) === selectedIndex"
          />
        </div>

        <!-- Courses Empty State -->
        <MentionEmptyState v-else icon-type="course" message="No courses found" />
      </div>

      <!-- Events Section -->
      <div v-if="filterType === 'all' || filterType === 'event'" class="section">
        <!-- Section Header -->
        <div class="section-header">
          <CPText size="xs" weight="medium" class="text-secondary">
            {{ t('editor.mentions.events') }}
          </CPText>
        </div>

        <!-- Events Items -->
        <div v-if="eventsSection.items.length > 0" class="section-items">
          <MentionsListItem
            v-for="item in eventsSection.items"
            :key="item.id"
            :item="item"
            :on-click="handleSelect"
            :is-selected="allItems.findIndex((i: MentionItem) => i.id === item.id) === selectedIndex"
          />
        </div>

        <!-- Events Empty State -->
        <MentionEmptyState v-else icon-type="event" message="No events found" />
      </div>

      <!-- Channels Section -->
      <div v-if="filterType === 'all' || filterType === 'channel'" class="section">
        <!-- Section Header -->
        <div class="section-header">
          <CPText size="xs" weight="medium" class="text-secondary">
            {{ t('editor.mentions.channels') }}
          </CPText>
        </div>

        <!-- Channels Items -->
        <div v-if="channelsSection.items.length > 0" class="section-items">
          <MentionsListItem
            v-for="item in channelsSection.items"
            :key="item.id"
            :item="item"
            :on-click="handleSelect"
            :is-selected="allItems.findIndex((i: MentionItem) => i.id === item.id) === selectedIndex"
          />
        </div>

        <!-- Channels Empty State -->
        <MentionEmptyState v-else icon-type="channel" message="No channels found" />
      </div>
    </div>

    <!-- Results without Sections -->
    <div v-else class="flat-results">
      <MentionsListItem
        v-for="(item, index) in allItems"
        :key="item.id"
        :item="item"
        :on-click="handleSelect"
        :is-selected="index === selectedIndex"
      />
    </div>
  </div>
</template>

<style scoped>
  .mentions-list {
    background: var(--cp-brand-background);
    border-radius: 8px;
    overflow: hidden;
    max-height: 24rem;
    overflow-y: auto;
  }

  /* Section styling */
  .section-header {
    background-color: var(--cp-brand-surface);
    border-bottom: 1px solid var(--cp-border-600);
    padding: 8px;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  .section-items {
    background: var(--cp-brand-background);
  }

  /* Loading and empty states */
  .loading-state,
  .empty-state {
    background: var(--cp-brand-background);
  }

  /* Scrollbar styling */
  .mentions-list::-webkit-scrollbar {
    width: 4px;
  }

  .mentions-list::-webkit-scrollbar-track {
    background: var(--cp-brand-surface);
  }

  .mentions-list::-webkit-scrollbar-thumb {
    background: var(--cp-border-600);
    border-radius: 2px;
  }

  .mentions-list::-webkit-scrollbar-thumb:hover {
    background: var(--cp-font-400);
  }
</style>
